import styled from "@emotion/styled";
import { Button, Checkbox, Link, Typography } from "@kaltura/ds-react-components";
import { Truncate } from "@kaltura/mediaspace-shared-ui";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";
import { LinearProgress, linearProgressClasses } from "@mui/material";

export const StyledMediaCard = styled("div")<{ active: boolean }>(({ theme, active }) => ({
    position: "relative",
    display: "flex",
    padding: active ? `calc(${theme.spacing(2)}  - 2px)` : theme.spacing(2),
    gap: theme.spacing(2),
    borderRadius: theme.kaltura.shape.roundness2,
    background: theme.kaltura.palette.surfaces.paper,
    border: active ? `2px solid ${theme.kaltura.palette.tone4}` : undefined,
    minHeight: 104,
}));

export const StyledThumbWrap = styled("div")(({ theme }) => ({
    minWidth: 186,
    width: 186,
    minHeight: 106,
    height: 106,

    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        minWidth: 158,
        width: 158,
        minHeight: 90,
        height: 90,
    },
}));

export const ScheduledBadgeWrap = styled("div")(({ theme }) => ({
    lineHeight: theme.spacing(2),
}));

export const StyledContent = styled("div")({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    minWidth: 0,
});

export const StyledInfoWrap = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
}));

export const StyledInfo = styled("div")({
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
});

export const StyledMediaInfoBar = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
    height: "100%",
    position: "relative",
}));

export const StyledExploreAndActionsWrap = styled("div")({
    display: "flex",
    flexDirection: "column",
});

export const StyledName = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone1,
    marginBottom: theme.spacing(1),
}));

export const StyledLink = styled(Link)({
    textDecoration: "none",
    display: "block",
    [`&:focus-visible`]: {
        ...VisibleFocusStyle,
    },
});

export const StyledDescription = styled(Truncate)<{ lines: number }>(({ theme, lines }) => ({
    ...theme.kaltura.typography.body2,
    color: theme.kaltura.palette.tone2,

    "&.truncation-error": {
        maxHeight: `${lines * 1.5}em`,
        overflow: "hidden",
    },
    "& a": {
        fontWeight: theme.kaltura.typography.fontWeightBold,
        textDecoration: "none",
    },
}));

export const StyledCheckbox = styled(
    Checkbox,
    {
        shouldForwardProp: (prop) => prop !== "inline",
    }
)<{ inline?: boolean }>(({ inline, theme }) => ({
    zIndex: 1,
    marginLeft: theme.spacing(1),
    ...(
        !inline && {
            position: "absolute",
            marginTop: theme.spacing(1),
        }
    ),
}));

export const StyledButton = styled(Button)(({ theme }) => ({
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        alignSelf: "flex-end",
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        position: "absolute",
        right: 0,
    },
}));

export const StyledExploreButtonWrap = styled("div")(({ theme }) => ({
    textAlign: "right",
}));

export const BorderLinearProgress = styled(
    LinearProgress,
    {
        shouldForwardProp: (prop) => prop !== "progresscolor",
    }
)<{ progresscolor?: string }>(({ theme, progresscolor }) => ({
    height: 6,
    borderBottomLeftRadius: theme.kaltura.shape.roundness1,
    borderBottomRightRadius: theme.kaltura.shape.roundness1,
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(255, 255, 255, 0.50)",
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: "rgba(255, 255, 255, 0.50)",
    },

    [`& .${linearProgressClasses.bar}`]: {
        backgroundColor: progresscolor || theme.kaltura.palette.primary.main,
    },
}));

export const StyledProgressWrapper = styled("div")({
    position: "relative",
    width: "100%",
    height: "100%",
});
